import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { generationsService, conversationsService, modelsService, userService, objectsService, uploadMedia } from '../services';
import videoProcessor from '../services/VideoProcessor';
import { saveMediaAssets, loadMediaAssets, saveClipState, loadClipState, restoreClipMedia, sanitizeClipForStorage } from '../utils/mediaStorage';
import { listPending, clearPending, markPending, createRequestId } from '../utils/pendingGenerations';
import assetsService from '../services/assets';
import { extractVideoFrames } from '../utils/videoUtils';

const AppContext = createContext();

// ── Persistent media deletion helpers ────────────────────────────────────────
// IDs the user has dismissed are stored in localStorage so they survive page
// reloads and aren't re-injected every time loadGenerations() runs.
const HIDDEN_MEDIA_KEY = 'helix_hidden_media_ids';
const getHiddenMediaIds = () => {
    try { return new Set(JSON.parse(localStorage.getItem(HIDDEN_MEDIA_KEY) || '[]')); }
    catch { return new Set(); }
};
const persistHideMedia = (id) => {
    try {
        const ids = getHiddenMediaIds();
        ids.add(id);
        localStorage.setItem(HIDDEN_MEDIA_KEY, JSON.stringify([...ids]));
    } catch {}
};

export const AppProvider = ({ children }) => {
    // User State
    const [user, setUser] = useState(null);

    // Chat State
    const [messages, setMessages] = useState([
        { id: 1, role: 'assistant', content: "Hello! I'm ready to help you create. I can generate text, images, videos, and audio. What should we work on today?" }
    ]);
    const [chatInput, setChatInput] = useState('');
    const [attachedAssets, setAttachedAssets] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [streamingContent, setStreamingContent] = useState('');

    // Model State
    const [selectedModel, setSelectedModel] = useState('claude-haiku-4-5'); // default model slug
    const [availableModels, setAvailableModels] = useState([]);

    // Conversation State
    const [currentConversationId, setCurrentConversationId] = useState(null);
    const [conversations, setConversations] = useState([]);

    // Media State
    const [timelineClips, setTimelineClips] = useState([]);
    const [selectedClipId, setSelectedClipId] = useState(null);
    // selectedClipIds is the *full* multi-selection set, populated by the
    // storyboard's box-select / Ctrl+click. selectedClipId tracks the single
    // "active" clip (last-clicked, owns the inspector). Both stay in sync —
    // selectedClipId is always the last entry of selectedClipIds (or null).
    const [selectedClipIds, setSelectedClipIds] = useState([]);
    // Separate from selectedClipId — controls whether the Node Inspector panel is open.
    // Single-click on a node sets selectedClipId (timeline highlight) only;
    // double-click or the Details button also sets inspectorClipId (opens inspector).
    const [inspectorClipId, setInspectorClipId] = useState(null);
    // Which clip is currently running a generation — drives "Generating…" overlay on HelixNode
    const [generatingClipId, setGeneratingClipId] = useState(null);
    // Pending generations keyed by clipId — survives page refresh via
    // utils/pendingGenerations.js. Drives the same loading overlay so a
    // user that refreshes mid-generation still sees "Generating…" on the
    // clip rather than thinking their work disappeared.
    const [pendingGenerations, setPendingGenerations] = useState({});

    // Public helpers -- wrap the localStorage utility AND update React
    // state in a single call so callers don't have to do both.
    // Each pending entry gets a stable requestId at creation time so the
    // recovery poll matches by ID, not by prompt text.
    const markGenerationPending = useCallback((clipId, info) => {
        const requestId = info.requestId || createRequestId();
        const entry = { ...info, requestId, startedAt: Date.now() };
        markPending(clipId, entry);
        setPendingGenerations((prev) => ({
            ...prev,
            [clipId]: entry,
        }));
        return requestId;
    }, []);
    const clearGenerationPending = useCallback((clipId) => {
        clearPending(clipId);
        setPendingGenerations((prev) => {
            if (!prev[clipId]) return prev;
            const next = { ...prev };
            delete next[clipId];
            return next;
        });
    }, []);
    const [storyboardEdges, setStoryboardEdges] = useState([]);

    // ── Undo / Redo ──────────────────────────────────────────────────────────
    // Classic two-stack model: pushHistory() snapshots state BEFORE a mutation.
    // undo() restores from undoStack; redo() re-applies from redoStack.
    // Both stacks are refs (avoid re-renders on push/pop); a lightweight version
    // counter triggers re-renders only when canUndo / canRedo actually changes.
    const undoStackRef = useRef([]);
    const redoStackRef = useRef([]);
    const [_historyVersion, _setHistoryVersion] = useState(0); // re-render trigger

    // Refs that always mirror the latest committed state — pushHistory reads from
    // these so it captures the CURRENT value even when called synchronously
    // before a state update has been batched.
    const timelineClipsRef = useRef([]);
    const storyboardEdgesRef = useRef([]);

    // Keep refs in sync with state (effects run in declaration order — these fire
    // before any downstream effects that call pushHistory).
    useEffect(() => { timelineClipsRef.current = timelineClips; }, [timelineClips]);
    useEffect(() => { storyboardEdgesRef.current = storyboardEdges; }, [storyboardEdges]);

    const pushHistory = useCallback(() => {
        const snapshot = {
            clips: timelineClipsRef.current.map(c => ({ ...c })),
            edges: [...storyboardEdgesRef.current],
        };
        undoStackRef.current.push(snapshot);
        if (undoStackRef.current.length > 60) undoStackRef.current.shift(); // ring buffer
        redoStackRef.current = [];                      // clear future on new action
        _setHistoryVersion(v => v + 1);
    }, []);

    const undo = useCallback(() => {
        if (undoStackRef.current.length === 0) return;
        // Save current state so redo can return here
        const current = {
            clips: timelineClipsRef.current.map(c => ({ ...c })),
            edges: [...storyboardEdgesRef.current],
        };
        redoStackRef.current.push(current);
        const snapshot = undoStackRef.current.pop();
        setTimelineClips(snapshot.clips.map(c => ({ ...c })));
        setStoryboardEdges([...snapshot.edges]);
        _setHistoryVersion(v => v + 1);
    }, [setTimelineClips, setStoryboardEdges]);

    const redo = useCallback(() => {
        if (redoStackRef.current.length === 0) return;
        const current = {
            clips: timelineClipsRef.current.map(c => ({ ...c })),
            edges: [...storyboardEdgesRef.current],
        };
        undoStackRef.current.push(current);
        const snapshot = redoStackRef.current.pop();
        setTimelineClips(snapshot.clips.map(c => ({ ...c })));
        setStoryboardEdges([...snapshot.edges]);
        _setHistoryVersion(v => v + 1);
    }, [setTimelineClips, setStoryboardEdges]);

    const canUndo = undoStackRef.current.length > 0;
    const canRedo  = redoStackRef.current.length > 0;

    // Objects (Characters, Locations, Styles, Brand Assets, Products, Vehicles, etc.)
    // Persisted via backend API. Local state is a cache.
    const [objects, setObjects] = useState([]);

    const loadObjects = useCallback(async () => {
        const token = localStorage.getItem('accessToken');
        if (!token) return;
        try {
            const response = await objectsService.list({ limit: 200 });
            const list = response.data?.objects || response.objects || [];
            // Normalize _id → id for frontend component compat
            setObjects(list.map(o => ({ ...o, id: o._id || o.id })));
        } catch (error) {
            console.error('Failed to load objects:', error);
            // Fall back to localStorage migration
            try {
                const saved = JSON.parse(localStorage.getItem('helix_objects') || '[]');
                if (saved.length > 0) setObjects(saved);
            } catch {}
        }
    }, []);

    const addObject = async (obj) => {
        try {
            const response = await objectsService.create(obj);
            const created = response.data?.object || response.object || response.data;
            const normalized = { ...created, id: created._id || created.id };
            setObjects(prev => [normalized, ...prev]);
            // Object reference images surface as virtual rows in /assets —
            // refresh the unified library so they appear in the storyboard
            // drawer + Project Media tab without a page reload.
            bumpLibrary();
            return normalized;
        } catch (error) {
            console.error('Failed to create object:', error);
            // Optimistic local fallback
            const local = { id: `obj_${Date.now()}`, createdAt: Date.now(), ...obj };
            setObjects(prev => [local, ...prev]);
            return local;
        }
    };

    const updateObject = async (id, updates) => {
        // Optimistic update
        setObjects(prev => prev.map(o => o.id === id ? { ...o, ...updates } : o));
        try {
            await objectsService.update(id, updates);
            bumpLibrary();
        } catch (error) {
            console.error('Failed to update object:', error);
            // Revert on failure? For now we keep the optimistic update.
        }
    };

    // Recently-deleted objects: kept in memory for ~12s after a delete so
    // the user can hit Undo on the toast. Backend already soft-deletes
    // (deletedAt + 48h TTL), so the *restore* call works for much longer
    // than the in-memory window — but past 12s the toast disappears and
    // the user would need a "Recently deleted" trash view to recover.
    // Stored shape: full object snapshot + deletedAt timestamp.
    const [recentlyDeletedObjects, setRecentlyDeletedObjects] = useState([]);

    const deleteObject = async (id) => {
        // Capture the full object BEFORE removing — needed for the undo
        // toast preview AND for the optimistic re-insert if restore fails.
        const snapshot = objects.find(o => o.id === id);
        // Optimistic delete from the live list
        setObjects(prev => prev.filter(o => o.id !== id));
        if (snapshot) {
            const expiresAt = Date.now() + 12_000;
            setRecentlyDeletedObjects(prev => [...prev, { ...snapshot, _deletedAt: Date.now(), _expiresAt: expiresAt }]);
            // Auto-clear from the recently-deleted stack after the window.
            setTimeout(() => {
                setRecentlyDeletedObjects(prev => prev.filter(o => o.id !== id));
            }, 12_000);
        }
        try {
            await objectsService.delete(id);
            bumpLibrary();
        } catch (error) {
            console.error('Failed to delete object:', error);
            // API delete failed — put the object back in the live list so
            // the UI doesn't lie about the persisted state.
            if (snapshot) {
                setObjects(prev => prev.some(o => o.id === id) ? prev : [snapshot, ...prev]);
                setRecentlyDeletedObjects(prev => prev.filter(o => o.id !== id));
            }
        }
    };

    // Restore a soft-deleted object. Calls the backend restore endpoint
    // (which clears deletedAt) then re-inserts into the local objects
    // list. Pulls the object out of the recentlyDeleted stack so the
    // undo toast can dismiss itself.
    const restoreObject = async (id) => {
        const stashed = recentlyDeletedObjects.find(o => o.id === id);
        try {
            const response = await objectsService.restore(id);
            const restored = response?.data?.object || response?.object || stashed;
            const normalized = restored ? { ...restored, id: restored._id || restored.id || id } : null;
            if (normalized) {
                setObjects(prev => prev.some(o => o.id === normalized.id) ? prev : [normalized, ...prev]);
            }
            setRecentlyDeletedObjects(prev => prev.filter(o => o.id !== id));
            bumpLibrary();
        } catch (error) {
            console.error('Failed to restore object:', error);
            // 404 means the TTL already hard-purged it — nothing more we can do.
            // Drop the stale entry from the recently-deleted stack so the
            // toast clears.
            setRecentlyDeletedObjects(prev => prev.filter(o => o.id !== id));
        }
    };

    // ── Project State ─────────────────────────────────────────────────────────
    const [projectId,   setProjectId]   = useState(null);
    const [projectName, setProjectName] = useState('Untitled Project');
    // Project Brief — the narrative north-star the prompt-enhancement composer
    // reads on every compose. Per-project, localStorage-persisted under
    // `helix_brief_${projectId}`. All fields optional. Editing surface is the
    // Project Brief panel in the storyboard toolbar.
    const [projectBrief, setProjectBriefState] = useState({});
    const setProjectBrief = useCallback((updates) => {
        setProjectBriefState(prev => {
            const next = { ...prev, ...updates };
            if (projectId && typeof localStorage !== 'undefined') {
                try { localStorage.setItem(`helix_brief_${projectId}`, JSON.stringify(next)); } catch { /* quota */ }
            }
            return next;
        });
    }, [projectId]);
    // Load brief from localStorage when projectId changes
    useEffect(() => {
        if (!projectId || typeof localStorage === 'undefined') { setProjectBriefState({}); return; }
        try {
            const raw = localStorage.getItem(`helix_brief_${projectId}`);
            setProjectBriefState(raw ? JSON.parse(raw) : {});
        } catch { setProjectBriefState({}); }
    }, [projectId]);

    const getProjectsList = useCallback(() => {
        try { return JSON.parse(localStorage.getItem('helix_projects') || '[]'); }
        catch { return []; }
    }, []);

    const saveCurrentProject = useCallback(() => {
        if (!projectId) return;
        const projects = getProjectsList();
        const idx = projects.findIndex(p => p.id === projectId);
        const entry = {
            id: projectId,
            name: projectName,
            updatedAt: Date.now(),
            createdAt: projects[idx]?.createdAt ?? Date.now(),
        };
        if (idx >= 0) projects[idx] = entry; else projects.unshift(entry);
        localStorage.setItem('helix_projects', JSON.stringify(projects));
        localStorage.setItem(`helix_proj_${projectId}_clips`, JSON.stringify({ clips: timelineClipsRef.current.map(sanitizeClipForStorage), edges: storyboardEdgesRef.current }));
        localStorage.setItem('helix_current_project', projectId);
    }, [projectId, projectName, getProjectsList]);

    const loadProject = useCallback(async (id) => {
        // Save current before switching
        saveCurrentProject();
        // Restore target project clips
        try {
            const raw = localStorage.getItem(`helix_proj_${id}_clips`);
            if (raw) {
                const { clips, edges } = JSON.parse(raw);
                setTimelineClips(clips || []);
                setStoryboardEdges(edges || []);
            } else {
                setTimelineClips([]);
                setStoryboardEdges([]);
            }
        } catch { setTimelineClips([]); setStoryboardEdges([]); }
        setProjectId(id);
        const projects = getProjectsList();
        const entry = projects.find(p => p.id === id);
        if (entry) setProjectName(entry.name);
        localStorage.setItem('helix_current_project', id);
    }, [saveCurrentProject, getProjectsList]);

    const createNewProject = useCallback((name = 'Untitled Project') => {
        saveCurrentProject();
        const id = `proj_${Date.now()}`;
        setProjectId(id);
        setProjectName(name);
        setTimelineClips([]);
        setStoryboardEdges([]);
        setProjectMedia([]);
        // Objects are USER-scoped (not project-scoped) on the server, so we
        // don't clear them here. Wiping the local cache on new-project just
        // hid them until the next page refresh, which looked like data loss.
        localStorage.setItem('helix_current_project', id);
    }, [saveCurrentProject]);

    // ── Toast notifications ───────────────────────────────────────────────────
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((message, type = 'success', duration = 3500) => {
        const id = `toast_${Date.now()}_${Math.random()}`;
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
    }, []);

    const dismissToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    // Credit Alert State
    const [creditAlert, setCreditAlert] = useState({
        isOpen: false,
        required: 0,
        available: 0
    });

    const openCreditAlert = (required, available) => {
        setCreditAlert({
            isOpen: true,
            required,
            available
        });
    };

    const closeCreditAlert = () => {
        setCreditAlert(prev => ({ ...prev, isOpen: false }));
    };

    // UI State — persisted to localStorage so orientation survives refresh / re-login
    const [aspectRatio, setAspectRatio] = useState(
        () => localStorage.getItem('helix_aspect_ratio') || '16:9'
    );

    // Global Playback State
    const [currentTime, setCurrentTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const showOffMode = false; // Show-Off Mode disabled — will be refined in a future release
    const setShowOffMode = () => {}; // no-op

    // Load available models (uses public endpoint)
    const loadModels = useCallback(async () => {
        try {
            const response = await modelsService.getActiveModels();
            // API returns { data: { models: [...] } }
            const models = response.data?.models || response.data || [];
            setAvailableModels(Array.isArray(models) ? models : []);
        } catch (error) {
            console.error('Failed to load models:', error);
            setAvailableModels([]);
        }
    }, []);

    // Load recent generations for Media Library
    const loadGenerations = useCallback(async () => {
        const token = localStorage.getItem('accessToken');
        if (!token) return;

        try {
            const response = await generationsService.getRecentGenerations();
            const generations = response.data?.generations || response.generations || [];

            // Map backend generations to frontend media assets
            const mappedMedia = generations.flatMap(gen => {
                const assets = [];
                const createdAt = new Date(gen.createdAt).getTime();

                if (gen.type === 'image' && gen.output?.images) {
                    gen.output.images.forEach((img, idx) => {
                        assets.push({
                            id: `${gen._id}_${idx}`,
                            type: gen.metadata?.type || 'image', // Support 'character' type from metadata
                            url: img.url,
                            name: gen.input?.prompt?.slice(0, 50) || 'Untitled Image',
                            metadata: {
                                ...gen.metadata,
                                prompt: gen.input?.prompt,
                                createdAt
                            }
                        });
                    });
                } else if (gen.type === 'video' && gen.output?.videoUrl) {
                    assets.push({
                        id: gen._id,
                        type: 'video',
                        url: gen.output.videoUrl,
                        name: gen.input?.prompt?.slice(0, 50) || 'Untitled Video',
                        metadata: {
                            ...gen.metadata,
                            prompt: gen.input?.prompt,
                            duration: gen.output.duration,
                            createdAt
                        }
                    });
                } else if (gen.type === 'audio' && gen.output?.audioUrl) {
                    assets.push({
                        id: gen._id,
                        type: 'audio',
                        url: gen.output.audioUrl,
                        name: gen.input?.text?.slice(0, 50) || 'Untitled Audio',
                        metadata: {
                            ...gen.metadata,
                            prompt: gen.input?.text, // Map text to prompt for consistency
                            duration: gen.output.durationMs ? gen.output.durationMs / 1000 : 0,
                            createdAt
                        }
                    });
                }

                return assets;
            });

            // Filter out: assets with no URL (broken/pending) + user-dismissed IDs
            const hiddenIds = getHiddenMediaIds();
            const validMedia = mappedMedia.filter(a => a.url && !hiddenIds.has(a.id));

            // Merge with existing user-uploaded files (pm_* IDs) instead of
            // replacing the whole array — preserves uploads that survive via IndexedDB.
            // Deduplicate by URL so optimistic pm_* adds don't duplicate backend items.
            // Prefer local blob-cached versions over remote URLs (they survive expiry).
            setProjectMedia(prev => {
                const backendIds  = new Set(validMedia.map(m => m.id));
                const backendUrls = new Set(validMedia.map(m => m.url).filter(Boolean));
                const localById   = new Map(prev.filter(m => m.blobData).map(m => [m.id, m]));
                const userUploads = prev.filter(m =>
                    (m.id?.startsWith('pm_') || m.blobData) &&
                    !backendIds.has(m.id) &&
                    !backendUrls.has(m.url)
                );
                // Use local blob-cached version if available, otherwise use server URL
                const merged = validMedia.map(m => localById.get(m.id) || m);
                return [...merged, ...userUploads];
            });

            // Background blob-caching of remote URLs is intentionally removed.
            // It used to fetch every asset.url client-side to keep a local blob
            // around in case kie.ai temp URLs expired — but those fetches CORS-
            // fail noisily in the console for cross-origin hosts (every kie temp
            // URL spams an error). The Asset library is the source of truth now;
            // generations get mirrored to R2 via the backend, so the persistence
            // job this code did is already handled server-side.
        } catch (error) {
            console.error('Failed to load generations:', error);
        }
    }, []);



    // Load conversations (auth required)
    const loadConversations = useCallback(async () => {
        const token = localStorage.getItem('accessToken');
        if (!token) return;
        try {
            const response = await conversationsService.getConversations();
            setConversations(response.data?.conversations || []);
        } catch (error) {
            if (error.response?.status !== 401) {
                console.error('Failed to load conversations:', error);
            }
        }
    }, []);

    // Load User Profile (auth required)
    const loadUser = useCallback(async () => {
        const token = localStorage.getItem('accessToken');
        if (!token) return;
        try {
            const userData = await userService.getProfile();
            setUser(userData.user || userData);
        } catch (error) {
            if (error.response?.status !== 401) {
                console.error('Failed to load user:', error);
            }
        }
    }, []);

    const updateUserPreferences = async (newPreferences) => {
        try {
            // Merge with existing preferences
            const currentPreferences = user?.preferences || {};
            const updatedPreferences = { ...currentPreferences, ...newPreferences };

            const response = await userService.updateProfile({ preferences: updatedPreferences });
            setUser(response.user || response);
            return response;
        } catch (error) {
            console.error('Failed to update preferences:', error);
            throw error;
        }
    };

    // Initial Data Load
    React.useEffect(() => {
        loadModels();
        loadGenerations();
        loadConversations();
        loadUser();
        loadObjects();
    }, [loadModels, loadGenerations, loadConversations, loadUser, loadObjects]);

    // Actions
    const attachAssetToChat = (asset) => {
        if (!attachedAssets.find(a => a.name === asset.name)) {
            setAttachedAssets([...attachedAssets, asset]);
        }
    };

    const detachAssetFromChat = (assetName) => {
        setAttachedAssets(attachedAssets.filter(a => a.name !== assetName));
    };

    const handleCreditError = (error) => {
        const msg = error.response?.data?.error?.message || error.message || '';

        // Check for "Insufficient credits" error signature
        if (msg.includes('Insufficient credits') || error.response?.status === 402) {
            // Parse "Required: X, Available: Y"
            const requiredMatch = msg.match(/Required:\s*(\d+)/);
            const availableMatch = msg.match(/Available:\s*(\d+)/);

            const required = requiredMatch ? parseInt(requiredMatch[1]) : 0;
            const available = availableMatch ? parseInt(availableMatch[1]) : 0;

            openCreditAlert(required, available);
            return true; // We handled it
        }
        return false; // Not a credit error
    };

    // Send message with SSE streaming + multi-turn context
    const sendMessage = useCallback(async () => {
        if (!chatInput.trim() && attachedAssets.length === 0) return;
        if (isGenerating) return;

        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: chatInput,
            attachments: [...attachedAssets]
        };

        setMessages(prev => [...prev, userMessage]);
        const savedInput = chatInput;
        setChatInput('');
        setAttachedAssets([]);
        setIsGenerating(true);
        setStreamingContent('');

        // Auto-create conversation if none exists
        let convId = currentConversationId;
        if (!convId) {
            try {
                const conv = await conversationsService.createConversation({
                    title: savedInput.slice(0, 60) || 'New Chat',
                });
                convId = conv.data?._id || conv._id;
                setCurrentConversationId(convId);
                setConversations(prev => [conv.data || conv, ...prev]);
            } catch (err) {
                console.warn('Auto-create conversation failed:', err);
            }
        }

        // Add placeholder for assistant message
        const assistantMessageId = Date.now() + 1;
        setMessages(prev => [...prev, {
            id: assistantMessageId,
            role: 'assistant',
            content: '',
            isStreaming: true
        }]);

        // Build multi-turn context (last 20 messages = ~10 turns)
        const history = messages.slice(-20).map(m => ({
            role: m.role,
            content: m.content,
        }));
        history.push({ role: 'user', content: savedInput });

        let accumulated = '';

        try {
            await generationsService.generateTextStream(
                {
                    model: selectedModel,
                    prompt: savedInput,
                    messages: history,
                    conversationId: convId,
                    maxTokens: 4096,
                },
                // onChunk
                (event) => {
                    if (event.type === 'chunk' && event.content) {
                        accumulated += event.content;
                        setMessages(prev => prev.map(msg =>
                            msg.id === assistantMessageId
                                ? { ...msg, content: accumulated, isStreaming: true }
                                : msg
                        ));
                    } else if (event.content && !event.type) {
                        // Fallback: raw content without type wrapper
                        accumulated += event.content;
                        setMessages(prev => prev.map(msg =>
                            msg.id === assistantMessageId
                                ? { ...msg, content: accumulated, isStreaming: true }
                                : msg
                        ));
                    }
                },
                // onComplete
                () => {
                    setMessages(prev => prev.map(msg =>
                        msg.id === assistantMessageId
                            ? { ...msg, isStreaming: false }
                            : msg
                    ));
                    setIsGenerating(false);
                },
                // onError
                (error) => {
                    console.error('Streaming failed:', error);
                    const errorMessage = error.message || 'Unknown error';

                    // Check for credit errors
                    if (!handleCreditError(error, errorMessage)) {
                        setMessages(prev => prev.map(msg =>
                            msg.id === assistantMessageId
                                ? {
                                    ...msg,
                                    content: accumulated || `Error: ${errorMessage}`,
                                    isStreaming: false,
                                    isError: !accumulated,
                                }
                                : msg
                        ));
                    }
                    setIsGenerating(false);
                }
            );
        } catch (error) {
            // Network-level fallback
            console.error('sendMessage failed:', error);
            setMessages(prev => prev.map(msg =>
                msg.id === assistantMessageId
                    ? {
                        ...msg,
                        content: accumulated || `Connection error: ${error.message}`,
                        isStreaming: false,
                        isError: !accumulated,
                    }
                    : msg
            ));
            setIsGenerating(false);
        }
    }, [chatInput, attachedAssets, selectedModel, currentConversationId, isGenerating, messages]);

    // Generate image
    const generateImage = useCallback(async (prompt, options = {}) => {
        try {
            setIsGenerating(true);
            const response = await generationsService.generateImage({
                model: options.model || 'nano-banana-pro',
                prompt,
                size: options.size || '1024x1024',
                quality: options.quality || 'standard',
                n: options.n || 1
            });
            setIsGenerating(false);

            // Optimistically add to project media logic removed to prevent duplicates with loadGenerations
            // The response usually returns the full object which we can add if needed, 
            // but if we trigger a refresh or if the socket/polling updates it, we get doubles.
            // Let's rely on the returned data immediately but check for existence or just return.

            // Actually, for immediate feedback, we should add it BUT use the real ID if available.
            if (response?.data?.images) {
                response.data.images.forEach((img, idx) => {
                    // only add if we don't have a sophisticated sync yet. 
                    // For now, let's DISABLE optimistic add and rely on the response or a manual fetch.
                    // Or better: Add it, but verify uniqueness in addMediaToProject?
                    // The user reported "It returned 2". One is likely the optimistic one, one is the real one loaded later.
                });

                // Better fix: Just trigger a reload logic or let the component handle it. 
                // We'll trust the user wants to see it INSTANTLY.
                // We will add it, but we need to ensure addMediaToProject filters duplicates.
            }

            return response;
        } catch (error) {
            if (!handleCreditError(error)) {
                console.error("Generation failed:", error);
            }
            setIsGenerating(false);
            throw error;
        }
    }, []);

    // Generate video
    const generateVideo = useCallback(async (prompt, options = {}) => {
        try {
            setIsGenerating(true);
            const response = await generationsService.generateVideo({
                model: options.model || 'kling-3',
                prompt,
                duration: options.duration || 5,
                aspectRatio: options.aspectRatio || '16:9',
                image: options.image,
                endImage: options.endImage
            });
            setIsGenerating(false);

            if (response?.data?.videoUrl) {
                addMediaToProject({
                    type: 'video',
                    url: response.data.videoUrl,
                    name: prompt.slice(0, 50),
                    metadata: {
                        prompt,
                        duration: response.data.duration,
                        createdAt: Date.now()
                    }
                });
            }

            return response;
        } catch (error) {
            if (!handleCreditError(error)) {
                console.error("Generation failed:", error);
            }
            setIsGenerating(false);
            throw error;
        }
    }, []);

    // Generate text (raw)
    const generateText = useCallback(async (prompt, options = {}) => {
        try {
            setIsGenerating(true);
            const response = await generationsService.generateText({
                model: options.model || selectedModel,
                prompt,
                ...options
            });
            setIsGenerating(false);
            return response; // Service already unwraps .data
        } catch (error) {
            if (!handleCreditError(error)) {
                console.error("Generation failed:", error);
            }
            setIsGenerating(false);
            throw error;
        }
    }, [selectedModel]);

    // Generate audio
    const generateAudio = useCallback(async (text, options = {}) => {
        try {
            setIsGenerating(true);
            const response = await generationsService.generateAudio({
                model: options.model || 'eleven-multilingual-v2',
                text,
                voiceId: options.voiceId
            });
            setIsGenerating(false);

            if (response?.data?.audioUrl) {
                addMediaToProject({
                    type: 'audio',
                    url: response.data.audioUrl,
                    name: text.slice(0, 50),
                    metadata: {
                        prompt: text,
                        duration: response.data.durationMs ? response.data.durationMs / 1000 : 0,
                        createdAt: Date.now()
                    }
                });
            }

            return response;
        } catch (error) {
            if (!handleCreditError(error)) {
                console.error("Generation failed:", error);
            }
            setIsGenerating(false);
            throw error;
        }
    }, []);

    // Create new conversation
    const createConversation = useCallback(async (title) => {
        try {
            const response = await conversationsService.createConversation({
                title: title || 'New Conversation'
            });
            const newConversation = response.data;
            setConversations(prev => [newConversation, ...prev]);
            setCurrentConversationId(newConversation._id);
            setMessages([{
                id: 1,
                role: 'assistant',
                content: "Hello! I'm ready to help you create. What should we work on?"
            }]);
            return newConversation;
        } catch (error) {
            console.error('Failed to create conversation:', error);
            throw error;
        }
    }, []);

    // Clear chat
    const clearChat = useCallback(() => {
        setMessages([{
            id: 1,
            role: 'assistant',
            content: "Hello! I'm ready to help you create. What should we work on today?"
        }]);
        setCurrentConversationId(null);
    }, []);

    // Switch to existing conversation — loads message history from backend
    const switchConversation = useCallback(async (conversationId) => {
        if (!conversationId) return;
        setCurrentConversationId(conversationId);
        try {
            const response = await conversationsService.getMessages(conversationId);
            const msgs = response.data?.messages || response.messages || [];
            if (msgs.length) {
                setMessages(msgs.map((m, i) => ({
                    id: m._id || i,
                    role: m.role,
                    content: m.content,
                    attachments: m.attachments,
                })));
            } else {
                setMessages([{
                    id: 1,
                    role: 'assistant',
                    content: "Hello! I'm ready to help you create. What should we work on?"
                }]);
            }
        } catch (error) {
            console.error('Failed to load conversation messages:', error);
        }
    }, []);

    // Normalize a clip's `track` slug to the canonical `V<n>` form.
    // Legacy callers passed `'video'` (mediaType-as-track confusion) and that
    // value got persisted. parseInt on the slug fails → falls back to V1.
    // Run this on every clip whenever we mutate the timeline so downstream
    // consumers (V1 sequence-pack here, handle visibility in HelixNode,
    // FloatingMediaPlayer's track resolver) see consistent values.
    const normalizeClipTrack = (raw) => {
        if (!raw || raw === 'V1') return 'V1';
        const n = parseInt(String(raw).slice(1), 10);
        return Number.isFinite(n) && n >= 1 ? `V${n}` : 'V1';
    };

    // Media CRUD
    const addClip = (track = 'V1', name = 'New Scene', insertIndex = -1, explicitId = null, position = null) => {
        pushHistory();
        const newClip = {
            id: explicitId || `c${Date.now()}`,
            track: normalizeClipTrack(track),
            name,
            start: 0,
            duration: 4,
            disconnected: false,
            position: position,
            generations: [], // persistent history of all generated images/videos for this clip
        };

        setTimelineClips(prev => {
            // Normalize every existing clip's track first so legacy clips
            // (track: 'video') participate in the V1 sequence-pack. Without
            // this, a brand-new clip would land at start=0 directly on top
            // of any legacy clip whose track field is still 'video'.
            let newClips = prev.map(c => ({ ...c, track: normalizeClipTrack(c.track) }));
            if (insertIndex >= 0 && insertIndex <= newClips.length) {
                newClips.splice(insertIndex, 0, newClip);
            } else {
                newClips.push(newClip);
            }

            // Sequence-pack V1 only. V1 is the "spine" — its clips are
            // auto-arranged in their array order so the storyboard graph
            // reflects 1:1 in the timeline. V2..V6 are independent overlay
            // tracks that keep their stored `start` values; running them
            // through the same cursor would yank previously-placed
            // overlays back to t=0 every time a V1 clip is added.
            let runningTime = 0;
            return newClips.map(clip => {
                if (clip.track === 'V1') {
                    const updated = { ...clip, start: runningTime };
                    runningTime += clip.duration;
                    return updated;
                }
                return clip;
            });
        });
        setSelectedClipId(newClip.id);
        setInspectorClipId(newClip.id); // New clips always open inspector
    };

    // ── Project Media Storage (IndexedDB-backed) ─────────────────────────────
    const [projectMedia, setProjectMedia] = useState([]);
    // Bumped after the silent migration completes (one-time, post-load).
    // Components that maintain their own asset list (Media Lab) listen to
    // this and refetch so newly-migrated items appear without a manual
    // click or page reload.
    const [lastMediaImportAt, setLastMediaImportAt] = useState(0);
    // Tracks whether the initial IndexedDB load has completed so we don't
    // wipe the database with the empty default state on first render.
    const mediaLoadedRef = useRef(false);
    // Tracks whether the initial clip/edge state load has completed.
    const clipStateLoadedRef = useRef(false);

    // ── Asset API → projectMedia normalizer ──────────────────────────────
    // The server's /assets endpoint returns a unified union of:
    //   - real Asset rows (generations + /media/upload uploads)
    //   - virtual rows synthesized from each Object's referenceImages
    // Map both shapes into the projectMedia entry shape that the
    // storyboard's MediaLibraryDrawer + ProjectMedia page consume.
    const apiAssetToProjectMedia = useCallback((a) => {
        const isVirtual = !!a?.virtual;
        const fallbackName = a.metadata?.originalName
            || (typeof a.prompt === 'string' && a.prompt.slice(0, 80))
            || (a.type === 'video' ? 'Video' : a.type === 'audio' ? 'Audio' : 'Image');
        return {
            id: String(a._id),
            projectId: a.projectId || null,
            type: a.type,
            url: a.url,
            name: fallbackName,
            createdAt: a.createdAt,
            favorite: !!a.favorite,
            metadata: a.metadata || {},
            // Provenance markers — used by removeMediaFromProject to route
            // deletes back to the right backend endpoint.
            _source: 'api',
            _serverId: String(a._id),
            _virtual: isVirtual,
            _objectId: a.objectId ? String(a.objectId) : null,
        };
    }, []);

    // Counter bumped by callers (generation success, object CRUD, etc.) to
    // force a library re-sync. The effect below subscribes; this lets any
    // call site request a refresh without holding a ref to syncLibrary.
    const [libraryRefreshTick, setLibraryRefreshTick] = useState(0);
    const bumpLibrary = useCallback(() => setLibraryRefreshTick((n) => n + 1), []);

    // Pull the canonical library from /assets (no projectId, no scope =
    // global) and merge with whatever is already in projectMedia. Local
    // entries (blob: uploads in flight that haven't been mirrored yet)
    // are preserved when they don't overlap by URL with a server entry.
    const syncLibraryFromApi = useCallback(async ({ initial = false } = {}) => {
        try {
            const res = await assetsService.list({ limit: 500 });
            const apiAssets = res?.data?.assets || res?.assets || [];
            const mapped = apiAssets.map(apiAssetToProjectMedia);
            const apiUrls = new Set(mapped.map((m) => m.url).filter(Boolean));
            setProjectMedia((prev) => {
                // Keep local entries that haven't surfaced server-side yet
                // (e.g. an in-flight upload, or a blob: drag-drop preview
                // that's still mirroring). Drop any prior _source: 'api'
                // entries — those are about to be replaced wholesale.
                const localOnly = prev.filter((m) =>
                    m._source !== 'api' && (!m.url || !apiUrls.has(m.url))
                );
                return [...mapped, ...localOnly];
            });
            if (initial) setLastMediaImportAt(Date.now());
        } catch (err) {
            // Non-fatal: keep whatever's already in projectMedia (IndexedDB
            // legacy items + locally-added). The next sync will retry.
            console.warn('[library-sync] failed', err?.message || err);
        }
    }, [apiAssetToProjectMedia]);

    // Load persisted assets + clip state once on mount
    useEffect(() => {
        // Clip state is synchronous (localStorage) — read it now so it's
        // available when the async IndexedDB load completes.
        const { clips: savedClips, edges: savedEdges } = loadClipState();

        // Objects are now loaded from the API via loadObjects() in the initial data load effect.
        // Legacy localStorage objects will be migrated on first API-backed load.

        // Load / initialise project
        const lastProjectId = localStorage.getItem('helix_current_project');
        if (lastProjectId) {
            setProjectId(lastProjectId);
            try {
                const projects = JSON.parse(localStorage.getItem('helix_projects') || '[]');
                const entry = projects.find(p => p.id === lastProjectId);
                if (entry?.name) setProjectName(entry.name);
            } catch { /* ignore */ }
        } else {
            // First-ever launch: mint a default project id
            const id = `proj_${Date.now()}`;
            setProjectId(id);
            localStorage.setItem('helix_current_project', id);
        }

        loadMediaAssets().then((saved) => {
            // Set refs BEFORE any setState calls so the auto-save effects
            // don't fire until both are marked as loaded.
            mediaLoadedRef.current    = true;
            clipStateLoadedRef.current = true;

            if (saved.length > 0) setProjectMedia(saved);

            // Restore timeline clips — reconcile blob URLs via _pmId → projectMedia
            if (savedClips?.length > 0) {
                setTimelineClips(restoreClipMedia(savedClips, saved));
            }
            if (savedEdges?.length > 0) {
                setStoryboardEdges(savedEdges);
            }

            // ── Unified library sync (canonical source: Asset API) ─────────
            // All three media surfaces (Media Lab, Project Media tab,
            // storyboard drawer) now read from the same projectMedia state.
            // We hydrate it from /assets on every load so the storyboard
            // drawer matches the personal library exactly — no more "I made
            // it on my laptop and it's missing on production." Object refs
            // come back as virtual rows from the same endpoint, so character
            // images surface alongside generations and uploads everywhere.
            // syncLibraryFromApi() below is the canonical refresh; this
            // first call also handles trash-sync as a side effect (trashed
            // URLs are excluded from the response).
            void syncLibraryFromApi({ initial: true });

            // ── Silent migration DISABLED ──────────────────────────────────
            // Used to re-upload blob: items from localStorage projectMedia to
            // R2 on every app load. Bug: each re-upload generates a NEW R2
            // UUID, so the same blob produced a fresh Asset row every page
            // load — the library kept growing without bound. The backend's
            // upsert-by-URL guard couldn't help because the URL was genuinely
            // new each time.
            //
            // The migration was a one-time legacy bridge from before the
            // unified Asset library shipped. The Asset table is now the
            // source of truth, written exclusively by:
            //   - generation.controller (image / video / audio mirrors)
            //   - media.controller (uploads via /media/upload)
            // All four mirror via findOneAndUpdate(upsert) so they're
            // idempotent. No frontend code should write to Asset directly.
            setLastMediaImportAt(Date.now());
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-persist projectMedia whenever the list changes — but only after initial load
    useEffect(() => {
        if (!mediaLoadedRef.current) return;
        saveMediaAssets(projectMedia);
    }, [projectMedia]);

    // Re-sync the library from the API whenever any caller bumps the tick.
    // Skips the very first render (tick=0) since the mount-time fetch
    // already covers it.
    useEffect(() => {
        if (libraryRefreshTick === 0) return;
        if (!mediaLoadedRef.current) return;
        void syncLibraryFromApi();
    }, [libraryRefreshTick, syncLibraryFromApi]);

    // Periodic background sync — keeps the storyboard drawer fresh when
    // generations finish in flows that don't go through the context's
    // generate* helpers (NodeInspector calls aiService directly). 30s is
    // gentle enough not to thrash the API but tight enough that a video
    // that finished while the user was scrubbing the timeline appears in
    // the drawer before they go looking for it. Pause when the tab is
    // hidden so we don't stack requests on a stale connection.
    useEffect(() => {
        // NOTE: previously had `if (!mediaLoadedRef.current) return;` at the
        // top of this effect, which prevented the interval from ever starting
        // because the effect runs once at mount before the async IndexedDB
        // load completes. Move the guard inside `tick` so the timer always
        // runs and skips early ticks until the load is done.
        const tick = () => {
            if (!mediaLoadedRef.current) return;
            if (typeof document !== 'undefined' && document.hidden) return;
            void syncLibraryFromApi();
        };
        const id = setInterval(tick, 30_000);
        return () => clearInterval(id);
    }, [syncLibraryFromApi]);

    // Auto-persist timeline clips + storyboard edges — but only after initial load
    useEffect(() => {
        if (!clipStateLoadedRef.current) return;
        saveClipState(timelineClips, storyboardEdges);
        // Also persist under project-specific key so multi-project switching works
        if (projectId) {
            try {
                const sanitized = timelineClips.map(sanitizeClipForStorage);
                localStorage.setItem(`helix_proj_${projectId}_clips`, JSON.stringify({ clips: sanitized, edges: storyboardEdges }));
            } catch { /* storage quota */ }
        }
    }, [timelineClips, storyboardEdges, projectId]);

    // Persist aspect ratio so orientation survives page reload / re-login
    useEffect(() => {
        localStorage.setItem('helix_aspect_ratio', aspectRatio);
    }, [aspectRatio]);

    // ── Pending generation recovery ──────────────────────────────────────────
    // The image/video generation HTTP request dies when the user refreshes
    // the page, but the upstream provider task and the backend Generation
    // record live on. We restore visual loading state from localStorage
    // markers, then poll /generations every 5s looking for a completed
    // record whose prompt matches our marker (within a -30s/+10min window).
    // When we find one, we attach its result URL to the clip — exactly what
    // the original request would have done if it hadn't been orphaned.
    useEffect(() => {
        // Restore markers on mount so the inspector + node cards show
        // "Generating…" immediately, not blank.
        const initial = listPending();
        if (Object.keys(initial).length > 0) {
            setPendingGenerations(initial);
        }
    }, []);

    useEffect(() => {
        const ids = Object.keys(pendingGenerations);
        if (ids.length === 0) return;
        let stopped = false;

        const tryRecover = async () => {
            if (stopped) return;
            try {
                const res = await generationsService.getGenerations({ status: 'completed', limit: 30 });
                const list = res?.data?.generations || res?.generations || [];
                const next = { ...pendingGenerations };
                let mutated = false;

                for (const [clipId, info] of Object.entries(pendingGenerations)) {
                    // Skip failed entries -- they must NEVER claim a future result.
                    if (info.status === 'failed') {
                        delete next[clipId];
                        clearPending(clipId);
                        mutated = true;
                        continue;
                    }
                    // TTL reaper: expire stale markers after 10 minutes.
                    if (Date.now() - info.startedAt > 10 * 60 * 1000) {
                        console.warn(`[pending-gen-poll] expiring stale marker for clip ${clipId}`);
                        delete next[clipId];
                        clearPending(clipId);
                        mutated = true;
                        continue;
                    }
                    // Match by requestId first (stable, no false positives).
                    // Fall back to prompt+timestamp only for legacy markers that
                    // predate the requestId migration.
                    const match = list.find((g) => {
                        const created = new Date(g.createdAt).getTime();
                        if (created < info.startedAt - 30_000) return false;
                        // Primary: match by requestId stored in generation metadata
                        if (info.requestId && g.metadata?.requestId === info.requestId) return true;
                        if (info.requestId && g.input?.requestId === info.requestId) return true;
                        // Legacy fallback: prompt match (only if entry has no requestId)
                        if (!info.requestId) {
                            const p = g.input?.prompt;
                            return p && p === info.prompt;
                        }
                        return false;
                    });
                    if (!match) continue;

                    if (info.kind === 'image') {
                        const url = match.output?.images?.[0]?.url;
                        if (url) {
                            setTimelineClips((prev) => prev.map((c) => c.id === clipId
                                ? {
                                    ...c,
                                    sourceMedia: url,
                                    sourceMediaType: 'image',
                                    startFrame: url,
                                    // Signal fade-in: the clip was just recovered
                                    _fadeIn: true,
                                    generations: [...(c.generations || []), { url, type: 'image', createdAt: Date.now() }],
                                }
                                : c
                            ));
                            delete next[clipId];
                            clearPending(clipId);
                            mutated = true;
                        }
                    } else if (info.kind === 'video') {
                        const url = match.output?.videoUrl || match.output?.images?.[0]?.url;
                        if (url) {
                            setTimelineClips((prev) => prev.map((c) => c.id === clipId
                                ? {
                                    ...c,
                                    media: url,
                                    mediaType: 'video',
                                    // Signal fade-in: the clip was just recovered
                                    _fadeIn: true,
                                    generations: [...(c.generations || []), { url, type: 'video', createdAt: Date.now() }],
                                }
                                : c
                            ));
                            delete next[clipId];
                            clearPending(clipId);
                            mutated = true;
                        }
                    }
                }

                if (mutated && !stopped) setPendingGenerations(next);
            } catch (err) {
                console.warn('[pending-gen-poll] failed:', err?.message || err);
            }
        };

        tryRecover();
        const interval = setInterval(tryRecover, 5000);
        return () => { stopped = true; clearInterval(interval); };
    }, [pendingGenerations]);

    // Objects are now persisted via backend API — no localStorage auto-save needed.

    // Auto-persist project name (debounced via useEffect — saves immediately on change)
    useEffect(() => {
        if (!projectId) return;
        const t = setTimeout(() => {
            try {
                const projects = JSON.parse(localStorage.getItem('helix_projects') || '[]');
                const idx = projects.findIndex(p => p.id === projectId);
                const entry = { id: projectId, name: projectName, updatedAt: Date.now(), createdAt: projects[idx]?.createdAt ?? Date.now() };
                if (idx >= 0) projects[idx] = entry; else projects.unshift(entry);
                localStorage.setItem('helix_projects', JSON.stringify(projects));
                localStorage.setItem('helix_current_project', projectId);
            } catch { /* ignore */ }
        }, 800);
        return () => clearTimeout(t);
    }, [projectName, projectId]);

    const addMediaToProject = (media) => {
        const entry = { id: `pm_${Date.now()}`, projectId, ...media };
        setProjectMedia(prev => {
            // Dedupe by URL — if this URL is already in the library
            // (typical case: a generation completed and the server-side
            // mirror already returned it via syncLibraryFromApi), we don't
            // want a second card with the same content.
            if (entry.url && prev.some(m => m.url === entry.url)) return prev;
            return [entry, ...prev];
        });

        // (Legacy blob-caching removed — see the comment in the loadMedia
        // path above. R2 mirroring on the backend covers persistence.)

        // For locally-uploaded files (blobData present + blob: URL), persist to R2
        // in the background so the URL survives page refresh and cross-device use.
        // Falls back silently if R2 isn't configured server-side.
        if (entry.blobData instanceof Blob && entry.url?.startsWith('blob:')) {
            const blobUrl = entry.url;
            const entryId = entry.id;
            uploadMedia(entry.blobData)
                .then(({ url: persistentUrl }) => {
                    if (!persistentUrl) return;
                    setProjectMedia(prev => prev.map(m => {
                        if (m.id !== entryId) return m;
                        // Free the local blob URL — we have a persistent one now
                        try { URL.revokeObjectURL(m.url); } catch {}
                        return { ...m, url: persistentUrl, persistentUrl };
                    }));
                    // ── Swap the blob: URL on any timeline clip that referenced
                    // this upload ──
                    // Without this, the projectMedia entry gets the persistent
                    // URL but the clip's `media` / `sourceMedia` / `startFrame`
                    // still point at the local blob URL — which dies the
                    // moment the user refreshes the page, losing the upload.
                    // Match by `_pmId` (set at drop time) AND by the raw blob:
                    // URL (defense-in-depth for any path that forgot the tag).
                    setTimelineClips(prev => prev.map(c => {
                        const matchesTag = c._pmId && c._pmId === entryId;
                        const matchesUrl = (c.media === blobUrl) || (c.sourceMedia === blobUrl) || (c.startFrame === blobUrl);
                        if (!matchesTag && !matchesUrl) return c;
                        const patch = {};
                        if (c.media === blobUrl) patch.media = persistentUrl;
                        if (c.sourceMedia === blobUrl) patch.sourceMedia = persistentUrl;
                        if (c.startFrame === blobUrl) patch.startFrame = persistentUrl;
                        if (c.endFrame === blobUrl) patch.endFrame = persistentUrl;
                        if (c.endMedia === blobUrl) patch.endMedia = persistentUrl;
                        // When `_pmId` matched but no field still references
                        // the blob URL (clip already moved on), no-op cleanly.
                        return Object.keys(patch).length > 0 ? { ...c, ...patch } : c;
                    }));
                })
                .catch(() => {}); // non-fatal — blob URL still works in this session
        }
    };

    const removeMediaFromProject = (id) => {
        persistHideMedia(id); // survive page reload — filtered out in loadGenerations

        // If the entry is API-backed, soft-delete it server-side so it
        // also vanishes from the personal library + project media tab.
        // Virtual rows (object reference images) can't be deleted via
        // /assets — those flow through the Object endpoint (user removes
        // the ref image from the object instead), so we just hide them
        // locally and leave the source object alone.
        const target = projectMedia.find(m => m.id === id);
        if (target && target._source === 'api' && !target._virtual && target._serverId) {
            assetsService.delete(target._serverId).catch((err) => {
                console.warn('[remove-media] server delete failed', err?.message || err);
            });
        }

        // Cascade: find the URL being removed and scrub it from every clip field
        // so nodes don't keep showing deleted images as their start/end frames,
        // and the generations carousel doesn't surface deleted history entries.
        const removed = projectMedia.find(m => m.id === id);
        if (removed?.url) {
            const url = removed.url;
            setTimelineClips(prev => prev.map(clip => {
                const touchedField  = clip.media === url || clip.sourceMedia === url ||
                                      clip.startFrame === url || clip.endFrame === url ||
                                      clip.endMedia === url;
                const touchedGen    = clip.generations?.some(g => g.url === url);
                if (!touchedField && !touchedGen) return clip;
                return {
                    ...clip,
                    media:            clip.media        === url ? null  : clip.media,
                    mediaType:        clip.media        === url ? null  : clip.mediaType,
                    sourceMedia:      clip.sourceMedia  === url ? null  : clip.sourceMedia,
                    sourceMediaType:  clip.sourceMedia  === url ? null  : clip.sourceMediaType,
                    startFrame:       clip.startFrame   === url ? null  : clip.startFrame,
                    endFrame:         clip.endFrame     === url ? null  : clip.endFrame,
                    endMedia:         clip.endMedia     === url ? null  : clip.endMedia,
                    generations:      (clip.generations || []).filter(g => g.url !== url),
                };
            }));
        }

        setProjectMedia(prev => prev.filter(m => m.id !== id));
    };

    const updateMediaItem = (id, updates) => {
        setProjectMedia(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
    };

    const updateClip = (id, updates) => {
        // Push undo snapshot for destructive changes (clearing media/frames/offset jumps)
        // but NOT for continuous adjustments (duration drag, position, minor tweaks)
        const destructiveKeys = ['media', 'sourceMedia', 'startFrame', 'endFrame', 'endMedia', 'mediaType', 'sourceMediaType', 'prompt'];
        const isDestructive = destructiveKeys.some(k =>
            k in updates && (updates[k] === null || updates[k] === undefined || updates[k] === '')
        );
        // Also treat large offset jumps as destructive (trim set in/out)
        const isOffsetChange = 'offset' in updates;
        if (isDestructive || isOffsetChange) pushHistory();

        setTimelineClips(prev => prev.map(clip => {
            if (clip.id !== id) return clip;

            // Enforce Max Duration (Source Media Check)
            if (updates.duration !== undefined) {
                const max = updates.maxDuration || clip.maxDuration;
                if (max && updates.duration > max) {
                    updates.duration = max;
                }
            }

            // Auto-populate Source Media from Media (Scene) if empty
            if (updates.media && !updates.sourceMedia && !clip.sourceMedia) {
                updates.sourceMedia = updates.media;
            }

            // Sync Start Frame & Scene Media from Source Media (if Source is being set)
            if (updates.sourceMedia) {
                // Auto-set startFrame from sourceMedia ONLY for images, not videos.
                // For videos, startFrame is captured asynchronously via extractVideoFrames.
                const srcType = updates.sourceMediaType || clip.sourceMediaType;
                const srcUrl = updates.sourceMedia;
                const isVidSrc = srcType === 'video' || (srcUrl && /\.(mp4|webm|mov)($|\?)/i.test(srcUrl));
                if (!isVidSrc && !updates.startFrame && !clip.startFrame) {
                    updates.startFrame = updates.sourceMedia;
                }

                // 2. Populate Main Scene Media if empty
                if (!updates.media && !clip.media) {
                    updates.media = updates.sourceMedia;
                    // Also sync the type (crucial for Blob URLs)
                    if (updates.sourceMediaType) {
                        updates.mediaType = updates.sourceMediaType;
                    } else if (clip.sourceMediaType) {
                        updates.mediaType = clip.sourceMediaType;
                    }
                }
            }

            return { ...clip, ...updates };
        }));

        // ── Async first/last-frame extraction for video sources ─────────────
        // When a clip becomes a video (via upload or generation) and doesn't
        // already have startFrame + endFrame, probe the video and persist
        // both as keyframes. This is what makes Bridge Mode work both for
        // AI-generated nodes AND for uploaded talking-head footage (the
        // "AI bridge → real footage → AI continuation" fusion workflow).
        // The frames also feed into HelixNode's start/end thumbnails on
        // the canvas, so a freshly uploaded video shows its real first
        // frame immediately as the storyboard panel.
        const justBecameVideo = (() => {
            const newVideoSrc = updates.sourceMedia || updates.media;
            if (!newVideoSrc) return null;
            const t = updates.sourceMediaType || updates.mediaType;
            const isVid = t === 'video' || /\.(mp4|webm|mov)($|\?)/i.test(newVideoSrc);
            return isVid ? newVideoSrc : null;
        })();
        // Skip if the caller explicitly provided both frames (avoid trampling
        // higher-quality frames the gen pipeline already produced).
        if (justBecameVideo && !(updates.startFrame && updates.endFrame)) {
            (async () => {
                try {
                    const { startFrame, endFrame, duration } = await extractVideoFrames(justBecameVideo, 4);
                    if (!startFrame && !endFrame) return;
                    setTimelineClips(prev => prev.map(c => {
                        if (c.id !== id) return c;
                        // Only fill in fields that are still missing — never
                        // overwrite explicit user-set values.
                        const patch = {};
                        if (startFrame && !c.startFrame) patch.startFrame = startFrame;
                        if (endFrame && !c.endFrame) patch.endFrame = endFrame;
                        if (typeof duration === 'number' && duration > 0 && !c.maxDuration) patch.maxDuration = duration;
                        return Object.keys(patch).length > 0 ? { ...c, ...patch } : c;
                    }));
                } catch { /* extraction failures are non-fatal; clip still works without keyframes */ }
            })();
        }
    };

    const deleteClip = (id) => {
        pushHistory();
        setTimelineClips(prev => prev.filter(clip => clip.id !== id));
        if (selectedClipId === id) setSelectedClipId(null);
        if (inspectorClipId === id) setInspectorClipId(null);
    };

    // Ripple delete: removes the clip AND shifts all subsequent clips left to close the gap
    const rippleDelete = useCallback((id) => {
        pushHistory();
        setTimelineClips(prev => {
            const target = prev.find(c => c.id === id);
            if (!target) return prev;
            const gapEnd = (target.start || 0) + (target.duration || 0);
            return prev
                .filter(c => c.id !== id)
                .map(c => (c.start || 0) >= gapEnd
                    ? { ...c, start: (c.start || 0) - (target.duration || 0) }
                    : c
                );
        });
        if (selectedClipId === id) setSelectedClipId(null);
        if (inspectorClipId === id) setInspectorClipId(null);
    }, [selectedClipId, setSelectedClipId, inspectorClipId, setInspectorClipId, pushHistory]);

    const duplicateClip = (id) => {
        pushHistory();
        const newId = `c${Date.now()}`;

        setTimelineClips(prev => {
            const sourceIndex = prev.findIndex(c => c.id === id);
            if (sourceIndex === -1) return prev;

            const source = prev[sourceIndex];
            const newClip = {
                ...source,
                id: newId,
                name: `${source.name} (Copy)`,
                // Place the node to the right of the source on the storyboard
                position: source.position
                    ? { x: source.position.x + 350, y: source.position.y }
                    : undefined,
            };

            // Insert right after the source clip
            const newClips = [...prev];
            newClips.splice(sourceIndex + 1, 0, newClip);

            // Recalculate all start times sequentially
            let runningTime = 0;
            return newClips.map(clip => {
                const updated = { ...clip, start: runningTime };
                runningTime += clip.duration;
                return updated;
            });
        });

        setSelectedClipId(newId);
    };

    // ── Split clip at a cut point (in source-video seconds) ─────────────────
    // Trims the original to end at cutTime, creates a new clip starting there.
    // Returns the new clip ID so the caller can create edges.
    const splitClip = (id, cutTimeInSource, startFrameUrl = null) => {
        pushHistory();
        const newId = `c${Date.now()}`;

        setTimelineClips(prev => {
            const idx = prev.findIndex(c => c.id === id);
            if (idx === -1) return prev;
            const src = prev[idx];
            const srcTrack = src.track || 'V1';
            const srcOffset = src.offset || 0;
            const srcDur = src.duration || 4;
            const cutPoint = Math.max(srcOffset + 0.5, Math.min(cutTimeInSource, srcOffset + srcDur - 0.5));
            const leftDur = cutPoint - srcOffset;
            const rightDur = srcOffset + srcDur - cutPoint;

            // Update original clip (left half) — keeps everything as-is,
            // just shorter. Its IN frame thumb still points to srcOffset
            // which is still the correct first-visible frame.
            const leftClip = { ...src, duration: leftDur };

            // Create right half. Drop the carried-over startFrame because
            // the source clip's startFrame was captured at srcOffset (the
            // beginning of the original clip), not at cutPoint. Without
            // clearing, every cut piece would show the same poster from
            // t=srcOffset instead of its own slice. Live-preview thumbs
            // re-derive from media + offset on render so this is safe.
            const rightClip = {
                ...src,
                id: newId,
                name: `${src.name} (Cut)`,
                offset: cutPoint,
                duration: rightDur,
                startFrame: startFrameUrl || null,
                endFrame: null,
                endMedia: null,
                endMediaType: null,
                // Clear position so the storyboard's auto-layout places
                // the right half by its new (start, track). The previous
                // "+350px from source" placement collided with overlay
                // clips that had their own auto-positions and made the
                // canvas read poorly after a cut.
                position: null,
            };

            const newClips = [...prev];
            newClips[idx] = leftClip;
            newClips.splice(idx + 1, 0, rightClip);

            // Sequence-pack only the cut's track. Clips on other tracks
            // keep their stored `start` so cutting a V1 clip doesn't
            // shove V2 overlays around. V1 still walks consecutively
            // because the storyboard graph treats V1 as the spine.
            let runningTime = 0;
            return newClips.map(clip => {
                if ((clip.track || 'V1') !== srcTrack) return clip;
                const updated = { ...clip, start: runningTime };
                runningTime += clip.duration;
                return updated;
            });
        });

        setSelectedClipId(newId);
        setInspectorClipId(newId);
        return newId;
    };

    return (
        <AppContext.Provider value={{
            // User
            user,
            updateUserPreferences,

            // Chat
            messages,
            setMessages,
            chatInput,
            setChatInput,
            attachedAssets,
            attachAssetToChat,
            detachAssetFromChat,
            sendMessage,
            isGenerating,
            streamingContent,
            clearChat,

            // Models
            selectedModel,
            setSelectedModel,
            availableModels,
            loadModels,

            // Conversations
            conversations,
            currentConversationId,
            setCurrentConversationId,
            loadConversations,
            createConversation,
            switchConversation,

            // Generation
            generateImage,
            generateVideo,
            generateAudio,
            generateText,

            // Media
            timelineClips,
            setTimelineClips,
            addClip,
            updateClip,
            deleteClip,
            rippleDelete,
            duplicateClip,
            splitClip,
            selectedClipId,
            setSelectedClipId,
            selectedClipIds,
            setSelectedClipIds,
            inspectorClipId,
            setInspectorClipId,
            generatingClipId,
            setGeneratingClipId,
            pendingGenerations,
            markGenerationPending,
            clearGenerationPending,
            storyboardEdges,
            setStoryboardEdges,
            pushHistory,
            undo,
            redo,
            canUndo,
            canRedo,

            // Objects (Characters, Locations, Styles, Brands, Products, Vehicles, etc.)
            objects,
            addObject,
            updateObject,
            deleteObject,
            restoreObject,
            recentlyDeletedObjects,
            loadObjects,

            // Project management
            projectId,
            projectName,
            projectBrief,
            setProjectBrief,
            setProjectName,
            saveCurrentProject,
            loadProject,
            createNewProject,
            getProjectsList,

            // UI
            aspectRatio,
            setAspectRatio,
            creditAlert,
            closeCreditAlert,

            // Toast notifications
            toasts,
            showToast,
            dismissToast,

            // Services
            videoProcessor,

            // Project Media
            projectMedia,
            addMediaToProject,
            removeMediaFromProject,
            updateMediaItem,
            lastMediaImportAt,
            // Force a re-pull of the unified library from /assets. Call after
            // a generation success or any flow that mutates server-side
            // assets but doesn't go through the context's generate* helpers.
            refreshLibrary: bumpLibrary,

            // Playback State
            currentTime,
            setCurrentTime,
            isPlaying,
            setIsPlaying,
            showOffMode,
            setShowOffMode,
        }}>
            {children}
        </AppContext.Provider>
    );
};

export const useApp = () => useContext(AppContext);
